import {
  localProductionEnabled,
  localProductionOnly,
} from './lib/localProduction'
import {
  appHome,
  ploteaApp,
  ploteaPath,
  qaPanel,
  qaPanelPath,
} from './lib/productProfile'
import { surroundingsPreviewUrl } from './lib/surroundingsPreview'
import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ServerCrash, Clock } from 'lucide-react'
import { AppLayout } from './components/Layout'
import { OnboardingScreen } from './components/OnboardingScreen'
import {
  hasPloteaPlan,
  PloteaNoPlanScreen,
} from './components/PloteaNoPlanScreen'
import { Button } from './components/ui/button'
import { logout } from './lib/endpoints/auth'
import { qk } from './lib/queryKeys'
import { TESTID } from './lib/testids'
import { useAuth, resolveRouteAccess } from './lib/auth'
import { LoginPage } from './pages/Login'
import {
  PloteaLoginPage,
  PloteaMagicLinkPage,
  PloteaVerifyLinkPage,
} from './pages/PloteaLogin'
import {
  PloteaInvitePage,
  PloteaResetPasswordPage,
  usePloteaPendingInvitation,
} from './pages/PloteaInvite'
import { PloteaPrivacyPage, PloteaTermsPage } from './pages/PloteaLegal'
import { PloteaOrganizationPage } from './pages/PloteaOrganization'
import { LoreAuthorizePage } from './pages/LoreAuthorize'
import { LoreOrganizationOperationPage } from './pages/LoreOrganizationOperation'
import { DesktopAuthorizePage } from './pages/DesktopAuthorize'
import { VerifyEmailPage } from './pages/VerifyEmail'
import { JobListPage } from './pages/JobList'
import { ProductAnalyticsAccess } from './components/ProductAnalyticsAccess'
import { siteEnabled, siteStandalone } from './lib/site'
import { apartmentWebEnabled } from './lib/apartment'

// Off-hot-path pages are code-split so they don't weigh down the initial bundle
// (the shadcn/Base UI runtime is heavy). Admin + settings load on demand.
const ProductionPage = lazy(() =>
  import('./pages/Production').then((m) => ({ default: m.ProductionPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/Settings').then((m) => ({ default: m.SettingsPage })),
)
const SitePage = lazy(() =>
  import('./pages/Site').then((m) => ({ default: m.SitePage })),
)
const StandaloneSitePage = lazy(() =>
  import('./pages/Site').then((m) => ({ default: m.StandaloneSitePage })),
)
const SiteScreeningPage = lazy(() =>
  import('./pages/Site').then((m) => ({ default: m.SiteScreeningPage })),
)
const SitePortfolioPage = lazy(() =>
  import('./pages/Site').then((m) => ({ default: m.SitePortfolioPage })),
)
const StandaloneSitePortfolioPage = lazy(() =>
  import('./pages/Site').then((m) => ({
    default: m.StandaloneSitePortfolioPage,
  })),
)
const ApartmentPage = lazy(() =>
  import('./apartment/ApartmentPage').then((m) => ({
    default: m.ApartmentPage,
  })),
)
const SiteDataSourcesPage = lazy(() =>
  import('./pages/Site').then((m) => ({ default: m.SiteDataSourcesPage })),
)
const StandaloneSiteDataSourcesPage = lazy(() =>
  import('./pages/Site').then((m) => ({
    default: m.StandaloneSiteDataSourcesPage,
  })),
)
const SiteReportPage = lazy(() =>
  import('./site/report/ReportPage').then((m) => ({
    default: m.SiteReportPage,
  })),
)
const SurroundingsPage = lazy(() =>
  import('./pages/Surroundings').then((m) => ({ default: m.SurroundingsPage })),
)
const AdminMachinesPage = lazy(() =>
  import('./pages/AdminMachines').then((m) => ({
    default: m.AdminMachinesPage,
  })),
)
const AdminOperationsPage = lazy(() =>
  import('./pages/AdminOperations').then((m) => ({
    default: m.AdminOperationsPage,
  })),
)
const AdminTestsPage = lazy(() =>
  import('./pages/AdminTests').then((m) => ({ default: m.AdminTestsPage })),
)
const AdminCodeAnalysisPage = lazy(() =>
  import('./pages/AdminCodeAnalysis').then((m) => ({
    default: m.AdminCodeAnalysisPage,
  })),
)
const AdminUsersPage = lazy(() =>
  import('./pages/AdminUsers').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminBackgroundJobsPage = lazy(() =>
  import('./pages/AdminBackgroundJobs').then((m) => ({
    default: m.AdminBackgroundJobsPage,
  })),
)
const AdminVersionsPage = lazy(() =>
  import('./pages/AdminVersions').then((m) => ({
    default: m.AdminVersionsPage,
  })),
)
const AdminOrganizationsPage = lazy(() =>
  import('./pages/AdminOrganizations').then((m) => ({
    default: m.AdminOrganizationsPage,
  })),
)
const AdminExternalApiKeysPage = lazy(() =>
  import('./pages/AdminExternalApiKeys').then((m) => ({
    default: m.AdminExternalApiKeysPage,
  })),
)
const AdminSettingsPage = lazy(() =>
  import('./pages/AdminSettings').then((m) => ({
    default: m.AdminSettingsPage,
  })),
)
const AdminProductAnalyticsPage = lazy(() =>
  import('./pages/AdminProductAnalytics').then((m) => ({
    default: m.AdminProductAnalyticsPage,
  })),
)
const HostAnalyticsTermsPage = lazy(() =>
  import('./pages/HostAnalyticsTerms').then((m) => ({
    default: m.HostAnalyticsTermsPage,
  })),
)
const OrganizationsPage = lazy(() =>
  import('./pages/Organizations').then((m) => ({
    default: m.OrganizationsPage,
  })),
)
const OrgMembersPage = lazy(() =>
  import('./pages/OrgMembers').then((m) => ({ default: m.OrgMembersPage })),
)
const OrgPluginAccessPage = lazy(() =>
  import('./pages/OrgPluginAccess').then((m) => ({
    default: m.OrgPluginAccessPage,
  })),
)
const RenderCatalogPage = lazy(() =>
  import('./pages/RenderCatalog').then((m) => ({
    default: m.RenderCatalogPage,
  })),
)
const RenderVersionDetailPage = lazy(() =>
  import('./pages/RenderVersionDetail').then((m) => ({
    default: m.RenderVersionDetailPage,
  })),
)
const BuildingReviewPage = lazy(() =>
  import('./pages/BuildingReview').then((m) => ({
    default: m.BuildingReviewPage,
  })),
)
const AssetHubPage = lazy(() =>
  import('./pages/AssetHub').then((m) => ({ default: m.AssetHubPage })),
)
const AssetHubDetailPage = lazy(() =>
  import('./pages/AssetHubDetail').then((m) => ({
    default: m.AssetHubDetailPage,
  })),
)
// TEMPORARY: unauthenticated per-org dashboard. Lazy so it never weighs on the
// app bundle, and outside `authLayoutRoute` so it runs no auth query at all.
const FloorplanPublicPage = lazy(() =>
  import('./pages/FloorplanPublic').then((m) => ({
    default: m.FloorplanPublicPage,
  })),
)
const FloorplanVersionDetailPage = lazy(() =>
  import('./pages/FloorplanVersionDetail').then((m) => ({
    default: m.FloorplanVersionDetailPage,
  })),
)
const PublicDashboardPage = lazy(() =>
  import('./pages/PublicDashboard').then((m) => ({
    default: m.PublicDashboardPage,
  })),
)

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-muted-foreground">{children}</div>
    </div>
  )
}

function ApiUnavailableScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background gap-4">
      <ServerCrash className="size-12 text-muted-foreground" />
      <div className="text-xl text-foreground">
        Serwis tymczasowo niedostepny
      </div>
      <Button onClick={() => window.location.reload()}>Sprobuj ponownie</Button>
    </div>
  )
}

/**
 * Shown to an authenticated user whose account is not beta-approved. It replaces
 * the entire app shell — no sidebar, no routes — so a pending account can do
 * nothing but read the notice and log out. Only reachable when beta mode is on
 * (otherwise `beta_approved` is always true).
 */
function PendingApprovalScreen() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const doLogout = () => {
    void logout().finally(() => {
      qc.removeQueries({ queryKey: qk.me() })
      void nav({ to: '/login', replace: true })
    })
  }
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center"
      data-testid={TESTID.betaPending}
    >
      <Clock className="size-12 text-muted-foreground" />
      <h1 className="text-xl font-bold text-foreground">
        Konto oczekuje na zatwierdzenie
      </h1>
      <p className="max-w-md text-muted-foreground">
        Twoje konto wymaga zatwierdzenia przez administratora. Otrzymasz dostep,
        gdy zostanie zaakceptowane.
      </p>
      <Button variant="outline" onClick={doLogout}>
        Wyloguj
      </Button>
    </div>
  )
}

/** Guard + shell for all protected routes (port of AuthGuard + AppLayout). */
function AuthedLayout() {
  const auth = useAuth()
  const nav = useNavigate()
  const access = resolveRouteAccess(auth, {})
  const isSiteReport = useRouterState({
    select: (state) => state.location.pathname === '/site/report',
  })
  const isApartment = useRouterState({
    select: (state) => state.location.pathname === '/apartment',
  })
  const isPloteaOrganization = useRouterState({
    select: (state) => ploteaApp && state.location.pathname === '/organization',
  })
  // Plotea: accept an invitation kept by `/invite` across the sign-in.
  usePloteaPendingInvitation(ploteaApp && auth.status === 'authenticated')

  useEffect(() => {
    if (typeof access === 'object') void nav({ to: '/login', replace: true })
  }, [access, nav])

  if (auth.status === 'loading') return <Centered>Loading...</Centered>
  if (auth.status === 'apiUnavailable') return <ApiUnavailableScreen />
  if (auth.status === 'unauthenticated') {
    return <Centered>Redirecting to login...</Centered>
  }
  // Authenticated but not beta-approved → only the pending-approval notice.
  if (!auth.user.beta_approved) return <PendingApprovalScreen />
  // Org-only account (personal disabled) with no active org → onboarding: it must
  // accept an invitation or enter an org before the app is usable.
  if (!auth.user.personal_scope && !auth.user.active_org) {
    return <OnboardingScreen />
  }
  // Plotea paywall: an active organization without a current plan gets the
  // plan screen instead of a Site that only sees 403 `product_unavailable`.
  // The organization page stays reachable: an organization is staffed first
  // and buys a plan later.
  if (ploteaApp && !hasPloteaPlan(auth.user) && !isPloteaOrganization)
    return <PloteaNoPlanScreen />
  const userName = auth.user.name || auth.user.email
  // Keep the same auth gates as Site, with a paper-only page outside the shell.
  if (isSiteReport) return <Outlet />
  // The apartment editor is full-screen: same auth gates, no app shell.
  if (isApartment) return <Outlet />
  return (
    <AppLayout
      userName={userName}
      isSystemAdmin={auth.user.is_system_admin}
      isProductAnalyticsReader={auth.user.is_product_analytics_reader}
    >
      <Outlet />
    </AppLayout>
  )
}

function IndexRedirect() {
  const nav = useNavigate()
  useEffect(() => {
    void nav({ to: appHome, replace: true })
  }, [nav])
  return <Centered>Redirecting...</Centered>
}

/**
 * Search params for job routes: `?preview=<jobId>` opens the job preview sheet;
 * `?new=1` opens the New Render sheet (Render Jobs list only, harmless elsewhere).
 */
interface JobPreviewSearch {
  preview?: string
  new?: true
}
const validatePreviewSearch = (
  search: Record<string, unknown>,
): JobPreviewSearch => ({
  preview: typeof search.preview === 'string' ? search.preview : undefined,
  // Only serialize `?new` when opening the sheet — omit it otherwise so plain
  // navigation to /jobs stays clean (no `?new=false` clinging to every URL).
  new:
    search.new === true || search.new === '1' || search.new === 'true'
      ? true
      : undefined,
})

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  beforeLoad: ({ location }) => {
    if (localProductionOnly && location.pathname !== '/production')
      throw redirect({ to: '/production' })
    if (
      siteStandalone &&
      !location.pathname.startsWith('/site') &&
      !(apartmentWebEnabled && location.pathname === '/apartment')
    )
      throw redirect({ to: '/site' })
    if (qaPanel && !qaPanelPath(location.pathname))
      throw redirect({ to: appHome })
    // The Plotea web app is Site only: no Archigma pages, `/` lands on /site.
    if (ploteaApp && !ploteaPath(location.pathname))
      throw redirect({ to: '/site' })
  },
})

// Public routes (no guard, no layout).
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Plotea signs in against its own identity service (plotea-auth).
  component: ploteaApp ? PloteaLoginPage : LoginPage,
})
// Plotea email links (docs/architecture/plotea.md, "Emails").
const ploteaMagicLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/magic-link',
  component: PloteaMagicLinkPage,
})
const ploteaVerifyLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: PloteaVerifyLinkPage,
})
const ploteaInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  component: PloteaInvitePage,
})
const ploteaResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: PloteaResetPasswordPage,
})
// Plotea legal documents, public (docs/architecture/plotea.md, "Legal pages").
const ploteaPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PloteaPrivacyPage,
})
const ploteaTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: PloteaTermsPage,
})
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email/$email',
  component: VerifyEmailPage,
})
// TEMPORARY: public job dashboard, keyed by organization slug. No guard.
const publicDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/public-dashboard/$orgSlug',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <PublicDashboardPage />
    </Suspense>
  ),
})
// Public interactive floor plan, like /tour/...: the Unit id in the link is the capability.
const floorplanPublicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plan/$unitId',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <FloorplanPublicPage />
    </Suspense>
  ),
})
const hostAnalyticsTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/host-analytics/host-analytics-v1',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <HostAnalyticsTermsPage />
    </Suspense>
  ),
})
const loreAuthorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lore/authorize',
  component: LoreAuthorizePage,
})
const desktopAuthorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/desktop/authorize',
  component: DesktopAuthorizePage,
})
const loreOrganizationOperationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lore/organization-operation',
  component: LoreOrganizationOperationPage,
})

// Pathless layout wrapping all protected routes.
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AuthedLayout,
})
const indexRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/',
  component: IndexRedirect,
})
const jobsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/jobs',
  component: JobListPage,
  validateSearch: validatePreviewSearch,
})
const renderCatalogRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/catalog',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <RenderCatalogPage />
    </Suspense>
  ),
})
const renderVersionRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/catalog/versions/$versionId',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <RenderVersionDetailPage />
    </Suspense>
  ),
})
const floorplanVersionRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/catalog/floorplans/$versionId',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <FloorplanVersionDetailPage />
    </Suspense>
  ),
})
const buildingReviewRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/building-review',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie…</Centered>}>
      <BuildingReviewPage />
    </Suspense>
  ),
})
const assetHubRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/asset-hub',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AssetHubPage />
    </Suspense>
  ),
})
const assetHubDetailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/asset-hub/assets/$assetId',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AssetHubDetailPage />
    </Suspense>
  ),
})
const apiKeysRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/settings/api-keys',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <SettingsPage />
    </Suspense>
  ),
})
// Plotea self-service organization settings (owner decision O4).
const ploteaOrganizationRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/organization',
  component: PloteaOrganizationPage,
})
const organizationsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/organizations',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <OrganizationsPage />
    </Suspense>
  ),
})
const orgMembersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/organizations/$orgId/members',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <OrgMembersPage />
    </Suspense>
  ),
})
const orgPluginAccessRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/organizations/$orgId/plugin-access',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <OrgPluginAccessPage />
    </Suspense>
  ),
})
const adminRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/machines',
  validateSearch: validatePreviewSearch,
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminMachinesPage />
    </Suspense>
  ),
})
const adminUsersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/users',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminUsersPage />
    </Suspense>
  ),
})
const adminOperationsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/operations',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminOperationsPage />
    </Suspense>
  ),
})
const adminTestsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/tests',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminTestsPage />
    </Suspense>
  ),
})
const adminCodeAnalysisRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/code-analysis',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminCodeAnalysisPage />
    </Suspense>
  ),
})
const adminVersionsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/versions',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminVersionsPage />
    </Suspense>
  ),
})
const adminBackgroundJobsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/jobs',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminBackgroundJobsPage />
    </Suspense>
  ),
})
const adminOrganizationsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/organizations',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminOrganizationsPage />
    </Suspense>
  ),
})
const adminExternalApiKeysRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/external-api-keys',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminExternalApiKeysPage />
    </Suspense>
  ),
})
const adminSettingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/scheduler-settings',
  component: () => (
    <Suspense fallback={<Centered>Loading...</Centered>}>
      <AdminSettingsPage />
    </Suspense>
  ),
})
const adminProductAnalyticsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/admin/product-analytics',
  component: () => (
    <ProductAnalyticsAccess>
      <Suspense fallback={<Centered>Loading...</Centered>}>
        <AdminProductAnalyticsPage />
      </Suspense>
    </ProductAnalyticsAccess>
  ),
})

const surroundingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/surroundings',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie otoczenia…</Centered>}>
      <SurroundingsPage />
    </Suspense>
  ),
})

const siteRoute = createRoute({
  getParentRoute: () => (siteStandalone ? rootRoute : authLayoutRoute),
  path: '/site',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie mapy…</Centered>}>
      {siteStandalone ? <StandaloneSitePage /> : <SitePage />}
    </Suspense>
  ),
})

const sitePortfolioRoute = createRoute({
  getParentRoute: () => (siteStandalone ? rootRoute : authLayoutRoute),
  path: '/site/portfolio',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie portfolio…</Centered>}>
      {siteStandalone ? <StandaloneSitePortfolioPage /> : <SitePortfolioPage />}
    </Suspense>
  ),
})

const siteScreeningRoute = createRoute({
  getParentRoute: () => (siteStandalone ? rootRoute : authLayoutRoute),
  path: '/site/screening',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie przesiewu…</Centered>}>
      <SiteScreeningPage />
    </Suspense>
  ),
})

const siteSourcesRoute = createRoute({
  getParentRoute: () => (siteStandalone ? rootRoute : authLayoutRoute),
  path: '/site/sources',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie źródeł danych…</Centered>}>
      {siteStandalone ? (
        <StandaloneSiteDataSourcesPage />
      ) : (
        <SiteDataSourcesPage />
      )}
    </Suspense>
  ),
})

const siteReportRoute = createRoute({
  getParentRoute: () => (siteStandalone ? rootRoute : authLayoutRoute),
  path: '/site/report',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie raportu…</Centered>}>
      <SiteReportPage />
    </Suspense>
  ),
})

// Apartment editor: behind the normal auth gates, rendered without the app shell.
const apartmentRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/apartment',
  component: () => (
    <Suspense fallback={<Centered>Wczytywanie generatora…</Centered>}>
      <ApartmentPage />
    </Suspense>
  ),
})

const productionRoute = createRoute({
  getParentRoute: () => (localProductionOnly ? rootRoute : authLayoutRoute),
  path: '/production',
  component: () => (
    <Suspense fallback={<Centered>Odczyt pipeline’u…</Centered>}>
      {localProductionOnly ? (
        <AppLayout
          userName="Operator lokalny"
          isSystemAdmin={false}
          isProductAnalyticsReader={false}
        >
          <ProductionPage />
        </AppLayout>
      ) : (
        <ProductionPage />
      )}
    </Suspense>
  ),
})

const routeTree = rootRoute.addChildren([
  ...(localProductionOnly ? [productionRoute] : []),
  ...(siteStandalone
    ? [
        siteRoute,
        sitePortfolioRoute,
        siteScreeningRoute,
        siteSourcesRoute,
        siteReportRoute,
      ]
    : []),
  loginRoute,
  verifyRoute,
  ...(ploteaApp
    ? [
        ploteaMagicLinkRoute,
        ploteaVerifyLinkRoute,
        ploteaInviteRoute,
        ploteaResetPasswordRoute,
        ploteaPrivacyRoute,
        ploteaTermsRoute,
      ]
    : []),
  publicDashboardRoute,
  floorplanPublicRoute,
  hostAnalyticsTermsRoute,
  loreAuthorizeRoute,
  desktopAuthorizeRoute,
  loreOrganizationOperationRoute,
  authLayoutRoute.addChildren([
    indexRoute,
    ...(ploteaApp ? [ploteaOrganizationRoute] : []),
    ...(apartmentWebEnabled ? [apartmentRoute] : []),
    ...(localProductionEnabled && !localProductionOnly
      ? [productionRoute]
      : []),
    jobsRoute,
    renderCatalogRoute,
    renderVersionRoute,
    floorplanVersionRoute,
    buildingReviewRoute,
    assetHubRoute,
    ...(surroundingsPreviewUrl ? [surroundingsRoute] : []),
    ...(siteEnabled && !siteStandalone
      ? [
          siteRoute,
          sitePortfolioRoute,
          siteScreeningRoute,
          siteSourcesRoute,
          siteReportRoute,
        ]
      : []),
    assetHubDetailRoute,
    apiKeysRoute,
    organizationsRoute,
    orgMembersRoute,
    orgPluginAccessRoute,
    adminRoute,
    adminOperationsRoute,
    adminCodeAnalysisRoute,
    adminTestsRoute,
    adminUsersRoute,
    adminOrganizationsRoute,
    adminVersionsRoute,
    adminBackgroundJobsRoute,
    adminExternalApiKeysRoute,
    adminSettingsRoute,
    adminProductAnalyticsRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  basepath: import.meta.env.BASE_URL,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
